// 🔧 Android Error Handler for ACCTOO Mobile App
// Handles Android-specific errors and provides graceful fallbacks

class AndroidErrorHandler {
  static isAndroid = typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform();

  // Handle AndroidAuthPersistence plugin errors
  static handleAuthPersistenceError(error) {
    console.warn('⚠️ AndroidAuthPersistence plugin error:', error.message);
    
    // Provide fallback behavior
    return {
      success: false,
      error: 'Authentication persistence not available',
      fallback: 'Using local storage fallback'
    };
  }

  // Handle Firebase Messaging errors
  static handleMessagingError(error) {
    console.warn('⚠️ Firebase Messaging error:', error.message);
    
    if (error.message.includes('AUTHENTICATION_FAILED')) {
      return {
        success: false,
        error: 'Firebase authentication failed',
        fallback: 'Skipping push notifications'
      };
    }
    
    return {
      success: false,
      error: 'Push notifications unavailable',
      fallback: 'Continuing without notifications'
    };
  }

  // Handle Firestore permission errors
  static handlePermissionError(error) {
    console.error('🔒 Firestore permission error:', error.message);
    
    if (error.code === 'permission-denied') {
      return {
        success: false,
        error: 'Permission denied',
        action: 'Check user authentication and security rules'
      };
    }
    
    return {
      success: false,
      error: 'Database access denied',
      action: 'Verify user permissions'
    };
  }

  // Handle biometric authentication errors
  static handleBiometricError(error) {
    console.warn('🔐 Biometric authentication error:', error.message);
    
    if (error.message.includes('not implemented')) {
      return {
        success: false,
        error: 'Biometric authentication not available',
        fallback: 'Using password authentication'
      };
    }
    
    return {
      success: false,
      error: 'Biometric authentication failed',
      fallback: 'Please use password login'
    };
  }

  // Generic error handler for Android-specific issues
  static handleAndroidError(error, context = 'Unknown') {
    console.error(`❌ Android error in ${context}:`, error);
    
    // Common Android error patterns
    if (error.message.includes('AndroidAuthPersistence')) {
      return this.handleAuthPersistenceError(error);
    }
    
    if (error.message.includes('FirebaseMessaging') || error.message.includes('AUTHENTICATION_FAILED')) {
      return this.handleMessagingError(error);
    }
    
    if (error.code === 'permission-denied') {
      return this.handlePermissionError(error);
    }
    
    if (error.message.includes('biometric') || error.message.includes('BiometricAuth')) {
      return this.handleBiometricError(error);
    }
    
    // Generic fallback
    return {
      success: false,
      error: error.message || 'Unknown error',
      context: context,
      fallback: 'Continuing with limited functionality'
    };
  }

  // Safe wrapper for async operations
  static async safeExecute(asyncFunction, context = 'Unknown') {
    try {
      return await asyncFunction();
    } catch (error) {
      return this.handleAndroidError(error, context);
    }
  }

  // Initialize error handling for the app
  static init() {
    if (!this.isAndroid) {
      return;
    }

    console.log('🔧 Android Error Handler initialized');

    // Global error handler for uncaught errors
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event) => {
        this.handleAndroidError(event.error, 'Global Error Handler');
      });

      window.addEventListener('unhandledrejection', (event) => {
        this.handleAndroidError(event.reason, 'Unhandled Promise Rejection');
      });
    }
  }
}

export default AndroidErrorHandler;


